import * as Dialog from '@radix-ui/react-dialog';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

import styles from './style.module.scss';

type ModalContent =
  | { type: 'embed'; title: string; embedCode: string }
  | { type: 'cards'; title: string; cards: OverflowCard[] };

type OverflowCard = {
  reviewerName: string;
  reviewerTitle: string;
  reviewTitle: string;
  rating: number;
  date: string;
  reviewText: string;
  reviewUrl: string;
  embedCode: string;
  avatarUrl: string;
  avatarColor: string;
  initials: string;
};

function decodeEmbedCode(encoded: string): string {
  try {
    return decodeURIComponent(atob(encoded));
  } catch {
    return '';
  }
}

function executeScripts(container: HTMLElement) {
  const scripts = Array.from(container.querySelectorAll('script'));
  scripts.forEach((oldScript) => {
    const newScript = document.createElement('script');
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.textContent = oldScript.textContent;
    if (oldScript.parentNode) {
      oldScript.parentNode.replaceChild(newScript, oldScript);
    } else {
      document.head.appendChild(newScript);
    }
  });
}

function collectAllCards(root: HTMLElement): OverflowCard[] {
  const grid = root.querySelector<HTMLElement>(`.${styles.reviewsGrid}`);
  if (!grid) return [];

  const allCards = Array.from(grid.querySelectorAll<HTMLElement>(':scope > article'));

  return allCards.map((card) => {
    const nameEl = card.querySelector<HTMLElement>(`.${styles.reviewerName}`);
    const titleEl = card.querySelector<HTMLElement>(`.${styles.reviewerTitle}`);
    const headlineEl = card.querySelector<HTMLElement>(`.${styles.reviewHeadline}`);
    const dateEl = card.querySelector<HTMLElement>(`.${styles.reviewDate}`);
    const excerptEl = card.querySelector<HTMLElement>(`.${styles.reviewExcerpt}`);
    const avatarEl = card.querySelector<HTMLElement>(`.${styles.avatar}`);
    const avatarImg = avatarEl?.querySelector<HTMLImageElement>('img');
    const readMoreLink = card.querySelector<HTMLAnchorElement>(`a.${styles.readMore}`);

    const starSvgs = Array.from(card.querySelectorAll(`.${styles.stars} svg`));
    let computedRating = 0;
    for (const svg of starSvgs) {
      if (svg.querySelector('clipPath')) {
        computedRating += 0.5;
      } else {
        const path = svg.querySelector('path');
        if (path && path.getAttribute('fill') === '#FF492C') {
          computedRating += 1;
        }
      }
    }

    return {
      reviewerName: nameEl?.textContent || '',
      reviewerTitle: titleEl?.textContent || '',
      reviewTitle: headlineEl?.textContent || '',
      rating: computedRating || 5,
      date: dateEl?.textContent || '',
      reviewText: excerptEl?.textContent || '',
      reviewUrl: readMoreLink?.href || '',
      embedCode: card.dataset.embedCode || '',
      avatarUrl: avatarImg?.src || '',
      avatarColor: avatarEl?.style.backgroundColor || '#5a39a2',
      initials: (!avatarImg && avatarEl?.textContent?.trim()) || '',
    };
  });
}

function hideOverflowCards(root: HTMLElement, visibleCount: number) {
  const grid = root.querySelector<HTMLElement>(`.${styles.reviewsGrid}`);
  if (!grid || visibleCount <= 0) return;

  const allCards = Array.from(grid.querySelectorAll<HTMLElement>(':scope > article'));
  allCards.forEach((card, index) => {
    if (index >= visibleCount) {
      card.classList.add(styles.hiddenCard);
    }
  });
}

function StarIcons({ rating }: { rating: number }) {
  const STAR_D =
    'M8 1.333l1.885 3.82 4.215.614-3.05 2.973.72 4.193L8 10.847l-3.77 1.986.72-4.193-3.05-2.973 4.215-.614L8 1.333z';
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (rating >= i) {
      stars.push(
        <svg key={i} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d={STAR_D} fill="#FF492C" />
        </svg>,
      );
    } else if (rating >= i - 0.5) {
      stars.push(
        <svg key={i} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <defs>
            <clipPath id={`halfLeft${i}`}>
              <rect x="0" y="0" width="8" height="16" />
            </clipPath>
            <clipPath id={`halfRight${i}`}>
              <rect x="8" y="0" width="8" height="16" />
            </clipPath>
          </defs>
          <path d={STAR_D} fill="#FF492C" clipPath={`url(#halfLeft${i})`} />
          <path d={STAR_D} fill="#E2DBD5" clipPath={`url(#halfRight${i})`} />
        </svg>,
      );
    } else {
      stars.push(
        <svg key={i} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d={STAR_D} fill="#E2DBD5" />
        </svg>,
      );
    }
  }
  return <span className={styles.stars}>{stars}</span>;
}

function OverflowCardItem({
  card,
  onEmbedClick,
}: {
  card: OverflowCard;
  onEmbedClick: (card: OverflowCard) => void;
}) {
  const hasEmbed = Boolean(card.embedCode);
  const hasUrl = Boolean(card.reviewUrl);

  return (
    <article className={styles.modalCard}>
      <div className={styles.cardHeader}>
        <div
          className={styles.avatar}
          aria-hidden="true"
          style={!card.avatarUrl ? { backgroundColor: card.avatarColor } : undefined}
        >
          {card.avatarUrl ? (
            <img src={card.avatarUrl} alt="" className={styles.avatarImage} />
          ) : (
            card.initials
          )}
        </div>
        <div className={styles.reviewerMeta}>
          <span className={styles.reviewerName}>{card.reviewerName}</span>
          <span className={styles.reviewerTitle}>{card.reviewerTitle}</span>
        </div>
      </div>

      <h3 className={styles.reviewHeadline}>{card.reviewTitle}</h3>

      <div className={styles.starsRow}>
        <StarIcons rating={card.rating} />
        {card.date && <span className={styles.reviewDate}>{card.date}</span>}
      </div>

      <p className={styles.reviewExcerpt}>{card.reviewText}</p>

      {hasEmbed ? (
        <button className={styles.readMore} type="button" onClick={() => onEmbedClick(card)}>
          Read the full Interact review
        </button>
      ) : hasUrl ? (
        <a
          className={styles.readMore}
          href={card.reviewUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Read the full Interact review
        </a>
      ) : null}
    </article>
  );
}

function OverflowCardsRenderer({
  cards,
  onEmbedClick,
}: {
  cards: OverflowCard[];
  onEmbedClick: (card: OverflowCard) => void;
}) {
  return (
    <div className={styles.modalCardList}>
      {cards.map((card, index) => (
        <OverflowCardItem key={index} card={card} onEmbedClick={onEmbedClick} />
      ))}
    </div>
  );
}

function EmbedRenderer({ embedCode }: { embedCode: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !embedCode) return;

    // Use rAF to ensure the Dialog portal DOM is fully committed
    const frameId = requestAnimationFrame(() => {
      el.innerHTML = embedCode;
      executeScripts(el);
    });

    return () => {
      cancelAnimationFrame(frameId);
      if (el) el.innerHTML = '';
    };
  }, [embedCode]);

  return <div ref={containerRef} className={styles.embedContainer} />;
}

function ReviewModal({
  content,
  onClose,
  onEmbedClick,
}: {
  content: ModalContent | null;
  onClose: () => void;
  onEmbedClick: (card: OverflowCard) => void;
}) {
  if (!content) return null;

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.modal} aria-describedby={undefined}>
          <div className={styles.modalHeader}>
            <Dialog.Title className={styles.modalTitle}>{content.title}</Dialog.Title>
            <Dialog.Close className={styles.closeButton} aria-label="Close">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Dialog.Close>
          </div>
          {content.type === 'embed' ? (
            <EmbedRenderer embedCode={content.embedCode} />
          ) : (
            <OverflowCardsRenderer cards={content.cards} onEmbedClick={onEmbedClick} />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ReviewsApp({ root }: { root: HTMLElement }) {
  const [modalContent, setModalContent] = useState<ModalContent | null>(null);
  const visibleCount = Number(root.dataset.visibleCards) || 0;
  const moreReviewsSource = root.dataset.moreReviewsSource || 'embed';

  // Hide overflow cards on mount
  useEffect(() => {
    if (visibleCount > 0) {
      hideOverflowCards(root, visibleCount);
    }
  }, [root, visibleCount]);

  const handleEmbedClick = useCallback((card: OverflowCard) => {
    setModalContent({
      type: 'embed',
      title: card.reviewTitle || card.reviewerName || 'Review',
      embedCode: decodeEmbedCode(card.embedCode),
    });
  }, []);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // "More reviews" trigger
      const allTrigger = target.closest<HTMLElement>('[data-all-reviews-trigger]');
      if (allTrigger) {
        if (moreReviewsSource === 'cards') {
          const allCards = collectAllCards(root);
          setModalContent({
            type: 'cards',
            title: 'Customer reviews',
            cards: allCards,
          });
        } else {
          const encoded = root.dataset.allReviewsEmbed;
          if (encoded) {
            setModalContent({
              type: 'embed',
              title: 'Customer reviews',
              embedCode: decodeEmbedCode(encoded),
            });
          }
        }
        return;
      }

      // Individual card "Read full review" trigger
      const cardTrigger = target.closest<HTMLElement>('[data-review-modal-trigger]');
      if (cardTrigger) {
        const card = cardTrigger.closest<HTMLElement>('[data-embed-code]');
        if (card) {
          setModalContent({
            type: 'embed',
            title: card.dataset.reviewTitle || card.dataset.reviewerName || 'Review',
            embedCode: decodeEmbedCode(card.dataset.embedCode || ''),
          });
        }
      }
    },
    [root, moreReviewsSource],
  );

  useEffect(() => {
    root.addEventListener('click', handleClick);
    return () => root.removeEventListener('click', handleClick);
  }, [root, handleClick]);

  return (
    <ReviewModal
      content={modalContent}
      onClose={() => setModalContent(null)}
      onEmbedClick={handleEmbedClick}
    />
  );
}

function initRatingAnimation(root: HTMLElement) {
  const summary = root.querySelector<HTMLElement>(`.${styles.ratingSummary}`);
  if (!summary) return;

  const ratingStrong = summary.querySelector<HTMLElement>(`.${styles.ratingText} strong`);
  if (!ratingStrong) return;

  const targetRating = parseFloat(ratingStrong.textContent || '');
  if (!targetRating || isNaN(targetRating)) return;

  // Respect reduced motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Set initial state: text at 0 (stars stay as-is, no animation on them)
  ratingStrong.textContent = '0/5';

  let animated = false;
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && !animated) {
        animated = true;
        observer.disconnect();
        runCountUp();
      }
    },
    { threshold: 0.5 },
  );
  observer.observe(summary);

  function runCountUp() {
    const duration = 2000;
    const pauseBeforeLast = 400;
    const lastStep = Math.floor(targetRating * 2) / 2; // e.g., 4.5
    const penultimate = lastStep - 0.5; // e.g., 4.0
    const start = performance.now();
    let paused = false;

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const current = targetRating * eased;
      const stepped = Math.floor(current * 2) / 2;

      // Pause at the penultimate step, then reveal the final value
      if (stepped >= penultimate && !paused) {
        paused = true;
        ratingStrong!.textContent = `${penultimate.toFixed(1)}/5`;
        setTimeout(() => {
          ratingStrong!.textContent = `${targetRating}/5`;
        }, pauseBeforeLast);
        return;
      }

      if (!paused) {
        ratingStrong!.textContent = `${stepped.toFixed(1)}/5`;
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }
}

const hydrateReviews = (root: HTMLElement) => {
  initRatingAnimation(root);

  const portalContainer = document.createElement('div');
  portalContainer.setAttribute('data-reviews-portal', 'true');
  root.appendChild(portalContainer);

  const mount = createRoot(portalContainer);
  mount.render(<ReviewsApp root={root} />);

  root.dataset.hydrated = 'true';
};

const selectBlocks = () =>
  Array.from(
    document.querySelectorAll<HTMLElement>('[data-reviews-root="true"][data-hydrated="false"]'),
  );

const start = () => {
  const hydrate = () => {
    selectBlocks().forEach(hydrateReviews);
  };

  const observer = new MutationObserver(hydrate);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  hydrate();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
